import { Heat } from 'functions/src/types/firestore/Game/Tournament/Heat';
import { HeatRules } from './HeatRules';
import { HeatTop3 } from './HeatTop3';
import { HeatLeaderboardTable } from './HeatLeaderboardTable';
import {
  LeaderboardContainer,
  LeaderboardContainerProps,
} from 'src/components/leaderboards/LeaderboardContainer';
import { Required } from 'utility-types';
import { Result } from 'functions/src/types/firestore/Game/Tournament/Heat/Result';
import { useMemo } from 'react';

export type ResultRanked = Result & {
  rank: number;
};

export type HeatLeaderboardProps = Pick<LeaderboardContainerProps, 'sponsors'> &
  Required<
    Pick<
      Heat<Date>,
      | 'title'
      | 'blurb'
      | 'startTime'
      | 'endTime'
      | 'qualification'
      | 'results'
      | 'scoreOption'
      | 'scoreOptions'
    >
  > & {
    handleTabChange: (tabName: string) => void;
  };

export const sortResults = (results: Result[], sortBy: 'asc' | 'desc') => {
  const defaultValue =
    sortBy === 'asc' ? Number.MAX_SAFE_INTEGER : -Number.MAX_SAFE_INTEGER;
  return results.sort((a, b) => {
    const scoreA = a?.score || defaultValue;
    const scoreB = b?.score || defaultValue;
    const priorityA = a?.priority || defaultValue;
    const priorityB = b?.priority || defaultValue;
    if (sortBy === 'asc') {
      if (scoreA !== scoreB) {
        return scoreA - scoreB;
      } else {
        return priorityA - priorityB;
      }
    } else {
      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      } else {
        return priorityB - priorityA;
      }
    }
  });
};

export const HeatLeaderboard = ({
  title,
  blurb,
  startTime,
  endTime,
  sponsors,
  handleTabChange,
  qualification,
  results,
  scoreOption,
  scoreOptions,
}: HeatLeaderboardProps) => {
  const sortedResults = sortResults(
    results || [],
    scoreOption?.orderBy || 'desc',
  );

  const resultsRanked = useMemo(() => {
    let rank = 1;
    return sortedResults.map((result, index, arr) => {
      if (index > 0) {
        const currentScore = result?.score;
        const previousScore = arr[index - 1]?.score;
        const currentPriority = result?.priority;
        const previousPriority = arr[index - 1]?.priority;

        if (
          currentScore !== previousScore ||
          currentPriority !== previousPriority
        ) {
          rank = index + 1;
        }
      }
      return { ...result, rank };
    });
  }, [sortedResults]);

  return (
    <LeaderboardContainer
      {...{ title, date: startTime, endDate: endTime, sponsors }}
      RightChildren={
        resultsRanked?.length > 0 ? (
          <>
            {resultsRanked?.slice?.(0, 3)?.every?.((result) => {
              return result?.score;
            }) && (
              <HeatTop3
                top3Results={resultsRanked.slice(0, 3)}
                scoreOption={scoreOption}
              />
            )}
            <HeatLeaderboardTable
              results={resultsRanked}
              qualification={qualification}
              scoreOption={scoreOption}
              scoreOptions={scoreOptions}
            />
          </>
        ) : undefined
      }
    >
      {blurb && (
        <HeatRules
          blurb={blurb}
          handleTabChange={handleTabChange}
          isQualifier={!!qualification}
        />
      )}
    </LeaderboardContainer>
  );
};
